import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    X, ListChecks, Plus, Trash2, Pencil, GripVertical,
    RadioIcon, MessageSquareText, BarChart3, Power, Loader2, ArrowUpToLine,
} from 'lucide-react';
import {
    fetchQuestions, deleteQuestion, reorderQuestions, updateQuestion, createQuestion,
} from '../../../api/survey.js';
import { SurveyQuestionEditDialog } from './SurveyQuestionEditDialog.jsx';
import { overlayDismiss } from '../../../lib/overlayDismiss';

/**
 * SurveyQuestionsModal — 問卷題目編輯
 *
 * 功能:題目清單 + 拖曳排序 + 啟用/停用 + 刪除 + 新增/編輯(走 SurveyQuestionEditDialog)。
 * 不支援檔案匯入 — 題目都從這裡手動新增,或從 build-survey-flow skill 一鍵生成。
 *
 * 設計遵循 frontend-design SKILL:Fraunces 大標、JetBrains Mono 顯示序號 +
 * 小寫題型標籤、cream/ink/accent palette、非對稱 padding、卡片 hover 換 accent 邊框。
 */

const QUESTION_TYPE_META = {
    choice: { icon: RadioIcon, label: 'CHOICE', toneKey: 'questionType.choice' },
    open: { icon: MessageSquareText, label: 'OPEN', toneKey: 'questionType.open' },
    rating: { icon: BarChart3, label: 'RATING', toneKey: 'questionType.rating' },
};

function pickQuestionTypeMeta(q) {
    return QUESTION_TYPE_META[q?.questionType] || QUESTION_TYPE_META.choice;
}

export function SurveyQuestionsModal({ open, onClose, avatarId, avatarName, sendMessage, isStreaming }) {
    const { t } = useTranslation('wsSurvey');
    const [questions, setQuestions] = useState([]);
    const [initialQuestions, setInitialQuestions] = useState([]);    // 開 modal 時的 snapshot,儲存時 diff 用
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [dragId, setDragId] = useState(null);
    const [hoverId, setHoverId] = useState(null);
    const dragOriginIndex = useRef(null);
    // edit dialog state — null = closed, {} = new question mode, q = edit existing
    const [editingQ, setEditingQ] = useState(null);
    const editOpen = editingQ !== null;

    // 「儲存」按鈕 — diff initialQuestions vs questions,batch 呼叫 API,
    // 完成後若 Q1 文字有變,發訊息給 AI 重生 MP3。
    const handleSave = useCallback(async () => {
        if (saving) return;
        setSaving(true);
        try {
            const initialMap = new Map(initialQuestions.map((q) => [q.id, q]));
            const currentMap = new Map(questions.map((q) => [q.id, q]));

            // 1) 刪掉 initial 有 / current 沒的(忽略 temp id,本來就不存在)
            const toDelete = initialQuestions.filter((q) => !currentMap.has(q.id));
            for (const q of toDelete) {
                await deleteQuestion(avatarId, q.id);
            }

            // 2) 新增(current 裡 id 開頭 'temp-' 的)→ 拿到真 id 後 patch 回 questions 陣列方便算 reorder
            const tempIdMap = new Map();    // temp id → real id
            for (const q of questions) {
                if (!String(q.id).startsWith('temp-')) continue;
                const payload = stripQuestionForApi(q);
                const r = await createQuestion(avatarId, payload);
                const realId = r?.data?.id || r?.id || r?.questionId;
                if (!realId) throw new Error(t('error.createNoId'));
                tempIdMap.set(q.id, realId);
            }

            // 3) 更新內容變動的(both 有,但欄位 differ)
            for (const q of questions) {
                if (String(q.id).startsWith('temp-')) continue;
                const init = initialMap.get(q.id);
                if (!init) continue;
                if (questionDiffers(q, init)) {
                    await updateQuestion(avatarId, q.id, stripQuestionForApi(q));
                }
            }

            // 4) 順序變動 → 一次 reorder。把 temp id 換成真 id。
            const finalIds = questions.map((q) =>
                String(q.id).startsWith('temp-') ? tempIdMap.get(q.id) : q.id
            );
            const initialIds = initialQuestions.map((q) => q.id);
            const orderChanged =
                finalIds.length !== initialIds.length
                || finalIds.some((id, i) => id !== initialIds[i]);
            if (orderChanged) {
                await reorderQuestions(
                    avatarId,
                    finalIds.map((id, i) => ({ questionId: id, newOrder: i + 1 })),
                );
            }

            // 5) Q1 文字真的有變才叫 AI 重生 MP3
            const initialQ1Text = (initialQuestions[0]?.question || '').trim();
            const currentQ1Text = (questions[0]?.question || '').trim();
            if (sendMessage && !isStreaming && initialQ1Text !== currentQ1Text && currentQ1Text) {
                const displayText = t('regenGreeting.displayText');
                const text = [
                    `問卷第一題剛剛變動。請用 generate-ivr-greeting skill 為這個 survey 角色重新生成開場白 MP3。`,
                    '',
                    `關鍵:`,
                    `- 角色是 newMode='survey',MP3 來源**從 canvas 第一題(entry node)抓題目文字**,不是 avatar.firstMessage`,
                    `- standalone script 已支援 SURVEY_MODE=auto,直接跑即可:`,
                    `  \`AVATAR_ID=${avatarId} NODE_ENV=production node scripts/generate-greeting-standalone.js\``,
                    `- 生成後 MP3 url 會寫進 avatars/${avatarId}.ivrFlowConfig.greetingMp3.mp3Url`,
                    `- 完成後幫我確認 mp3Url + duration + text 都對得上現在 Q1`,
                ].join('\n');
                sendMessage(text, { displayText });
            }

            onClose();
        } catch (e) {
            alert(t('error.saveFailed', { msg: e.message || e }));
        } finally {
            // ✅ 成功路徑也要解除「儲存中」。否則 saving 永遠停在 true，
            //    而此 modal 在 WorkshopPane 永遠掛載(open 只控 return null,state 不卸載),
            //    下次開啟按鈕仍顯示「儲存中…」+ handleSave 首行 if(saving)return → 再也存不了。
            setSaving(false);
        }
    }, [saving, initialQuestions, questions, avatarId, sendMessage, isStreaming, onClose, t]);

    // X / backdrop 用這個 — 不發 AI 訊息、不存 DB,直接丟棄變更
    const handleSilentClose = useCallback(() => {
        if (saving) return;
        const dirty = questions.length !== initialQuestions.length
            || questions.some((q, i) => q.id !== initialQuestions[i]?.id || questionDiffers(q, initialQuestions[i]));
        if (dirty && !confirm(t('confirm.unsavedClose'))) return;
        onClose();
    }, [saving, questions, initialQuestions, onClose, t]);

    const loadList = useCallback(async () => {
        if (!avatarId) return;
        setLoading(true);
        setError(null);
        try {
            const r = await fetchQuestions(avatarId);
            const list = Array.isArray(r) ? r : (r?.questions || r?.data || []);
            list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            setQuestions(list.map((q) => ({ ...q })));
            // snapshot:儲存時用來 diff 哪些題被刪、新增、改、reorder
            setInitialQuestions(list.map((q) => ({ ...q, customOptions: q.customOptions ? [...q.customOptions] : undefined })));
        } catch (e) {
            setError(e.message || t('error.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [avatarId, t]);

    useEffect(() => {
        // 開關都重置 saving — 此元件常駐掛載,防止上一輪殘留的 saving=true 卡死下一次存檔
        setSaving(false);
        if (open) loadList();
        else { setQuestions([]); setInitialQuestions([]); setError(null); }
    }, [open, loadList]);

    // 所有 mutation 都只動 local state。儲存時 diff initialQuestions 才呼叫 API。
    const handleDelete = (q) => {
        if (!confirm(t('confirm.deleteQuestion', { question: truncate(q.question, 40) }))) return;
        setQuestions((prev) => prev.filter((x) => x.id !== q.id));
    };

    // 置頂 — 把這題挪到 order=1,其他往後挪(純 local state)
    const handlePlaceTop = (q) => {
        if (questions[0]?.id === q.id) return;
        const others = questions.filter((x) => x.id !== q.id);
        setQuestions([
            { ...q, order: 1 },
            ...others.map((x, i) => ({ ...x, order: i + 2 })),
        ]);
    };

    const handleToggleActive = (q) => {
        setQuestions((prev) => prev.map((x) => (x.id === q.id ? { ...x, isActive: !(x.isActive !== false) } : x)));
    };

    // ── Drag & drop reorder ───────────────────────────────────────
    const onDragStart = (e, q, idx) => {
        setDragId(q.id);
        dragOriginIndex.current = idx;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', q.id);
    };
    const onDragOver = (e, q) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (q.id !== hoverId) setHoverId(q.id);
    };
    const onDrop = (e, target) => {
        e.preventDefault();
        const sourceId = dragId;
        setHoverId(null);
        setDragId(null);
        if (!sourceId || sourceId === target.id) return;
        const srcIdx = questions.findIndex((x) => x.id === sourceId);
        const dstIdx = questions.findIndex((x) => x.id === target.id);
        if (srcIdx < 0 || dstIdx < 0) return;
        const next = [...questions];
        const [moved] = next.splice(srcIdx, 1);
        next.splice(dstIdx, 0, moved);
        setQuestions(next.map((q, i) => ({ ...q, order: i + 1 })));
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm grid place-items-center p-4"
            {...overlayDismiss(handleSilentClose)}
        >
            <div
                className="bg-cream-50 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* ── Header — editorial title block ── */}
                <header className="px-7 py-5 border-b border-cream-200">
                    <div className="flex items-start justify-between gap-6">
                        <div className="min-w-0">
                            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-accent-700 mb-1">
                                Survey / Questions
                            </div>
                            <h2
                                className="text-2xl text-ink-900 leading-tight"
                                style={{ fontFamily: '"Fraunces", "Noto Serif TC", serif', fontWeight: 500 }}
                            >
                                {t('questions.title')}
                            </h2>
                            {avatarName && (
                                <p className="text-[12px] text-ink-500 mt-1">
                                    {avatarName} <span className="text-ink-300 mx-1">·</span>{' '}
                                    <span className="font-mono text-[10px]">{avatarId?.slice(0, 8)}</span>
                                </p>
                            )}
                        </div>
                        <button
                            onClick={handleSilentClose}
                            className="text-ink-400 hover:text-ink-900 transition-colors -mt-1"
                            title={t('questions.closeTitle')}
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Actions row */}
                    <div className="flex items-center gap-2 mt-5">
                        <button
                            onClick={() => setEditingQ({})}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] rounded-md border border-accent-300 bg-accent-50 text-accent-700 hover:bg-accent-100 hover:border-accent-500 transition-colors"
                        >
                            <Plus size={13} strokeWidth={1.8} /> {t('questions.add')}
                        </button>
                        <div className="ml-auto font-mono text-[11px] text-ink-400 tabular-nums">
                            {loading ? t('common:state.loading') : t('questions.count', { count: questions.length })}
                        </div>
                    </div>
                </header>

                {/* ── Body — list ── */}
                <div className="flex-1 overflow-y-auto px-7 py-5 bg-cream-100/30">
                    {loading && questions.length === 0 && (
                        <div className="flex items-center justify-center py-20 text-ink-400">
                            <Loader2 className="animate-spin" size={18} />
                            <span className="ml-2 text-sm">{t('questions.loading')}</span>
                        </div>
                    )}

                    {error && (
                        <div className="border border-accent-300 bg-accent-50 text-accent-700 text-sm rounded-md px-4 py-3 mb-4">
                            {error}
                        </div>
                    )}

                    {!loading && !error && questions.length === 0 && (
                        <div className="text-center py-20">
                            <ListChecks size={32} className="mx-auto text-accent-500/40" strokeWidth={1.4} />
                            <h3
                                className="text-lg text-ink-700 mt-4 mb-1"
                                style={{ fontFamily: '"Fraunces", "Noto Serif TC", serif' }}
                            >
                                {t('questions.emptyTitle')}
                            </h3>
                            <p className="text-[12px] text-ink-500 max-w-xs mx-auto leading-relaxed">
                                {t('questions.emptyHint')}
                            </p>
                        </div>
                    )}

                    <ul className="space-y-2">
                        {questions.map((q, idx) => (
                            <QuestionRow
                                key={q.id}
                                q={q}
                                idx={idx}
                                isDraggingThis={dragId === q.id}
                                isHover={hoverId === q.id && dragId && dragId !== q.id}
                                busy={false}
                                onDragStart={onDragStart}
                                onDragOver={onDragOver}
                                onDragLeave={() => setHoverId(null)}
                                onDragEnd={() => { setDragId(null); setHoverId(null); }}
                                onDrop={onDrop}
                                onDelete={() => handleDelete(q)}
                                onToggleActive={() => handleToggleActive(q)}
                                onEdit={() => setEditingQ(q)}
                                onPlaceTop={() => handlePlaceTop(q)}
                                isFirst={idx === 0}
                            />
                        ))}
                    </ul>
                </div>

                {/* ── Footer ── */}
                <footer className="px-7 py-3 border-t border-cream-200 flex items-center justify-between bg-cream-50">
                    <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-ink-400">
                        Drag · Reorder · Edit · Delete
                    </p>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-1.5 text-[13px] rounded-md bg-ink-900 hover:bg-ink-700 text-cream-50 transition-colors disabled:opacity-60 disabled:cursor-wait"
                        title={t('questions.saveTitle')}
                    >
                        {saving ? t('saving') : t('common:actions.save')}
                    </button>
                </footer>
            </div>

            {/* ── New / Edit dialog(疊在 list 上層,z-[60])── */}
            <SurveyQuestionEditDialog
                open={editOpen}
                onClose={() => setEditingQ(null)}
                avatarId={avatarId}
                question={editingQ?.id ? editingQ : null}
                onSaved={(payload, meta) => {
                    // payload 是 dialog 組好的 question fields(尚未進 DB)。父層 merge 進 local state,
                    // 等使用者按「儲存」才 diff + batch sync。
                    if (meta?.isEdit && editingQ?.id) {
                        // 編輯既有 → patch
                        setQuestions((prev) => prev.map((q) =>
                            q.id === editingQ.id ? { ...q, ...payload } : q
                        ));
                    } else {
                        // 新增 → temp id,先放最後;若 placeTop 再 reorder
                        const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
                        const newQ = { id: tempId, order: questions.length + 1, ...payload };
                        setQuestions((prev) => {
                            if (meta?.placeTop) {
                                return [
                                    { ...newQ, order: 1 },
                                    ...prev.map((x, i) => ({ ...x, order: i + 2 })),
                                ];
                            }
                            return [...prev, newQ];
                        });
                    }
                }}
            />
        </div>
    );
}

// ── Sub-component: each question row ─────────────────────────────────

function QuestionRow({
    q, idx, isDraggingThis, isHover, busy, isFirst,
    onDragStart, onDragOver, onDragLeave, onDragEnd, onDrop,
    onDelete, onToggleActive, onEdit, onPlaceTop,
}) {
    const { t } = useTranslation('wsSurvey');
    const meta = pickQuestionTypeMeta(q);
    const Icon = meta.icon;
    const isActive = q.isActive !== false;
    const options = q.useTemplate
        ? [{ label: t('row.templateOption', { id: q.templateId?.slice(0, 8) || 'tpl' }), isTemplate: true }]
        : (q.customOptions || []);

    return (
        <li
            draggable
            onDragStart={(e) => onDragStart(e, q, idx)}
            onDragOver={(e) => onDragOver(e, q)}
            onDragLeave={onDragLeave}
            onDragEnd={onDragEnd}
            onDrop={(e) => onDrop(e, q)}
            className={[
                'group relative bg-white rounded-md border transition-all duration-150',
                'flex items-start gap-3 px-3 py-3',
                isDraggingThis ? 'opacity-40' : '',
                isHover ? 'border-accent-500 shadow-[0_4px_16px_rgba(200,74,44,0.18)] -translate-y-px' : 'border-cream-200 hover:border-accent-300',
                !isActive ? 'bg-cream-100/50' : '',
            ].join(' ')}
        >
            {/* Drag handle + order tag */}
            <div className="flex flex-col items-center pt-0.5 select-none">
                <GripVertical size={14} className="text-ink-300 group-hover:text-accent-500 cursor-grab active:cursor-grabbing" />
                <span className="font-mono text-[10px] tabular-nums text-ink-400 mt-1">
                    {String(idx + 1).padStart(2, '0')}
                </span>
            </div>

            {/* Body */}
            <div className="flex-1 min-w-0">
                {/* Type chip + flags */}
                <div className="flex items-center gap-2 mb-1.5">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-accent-50 border border-accent-100">
                        <Icon size={10} className="text-accent-700" strokeWidth={2} />
                        <span
                            className="font-mono text-[9px] tracking-[0.16em] text-accent-700"
                            style={{ fontWeight: 600 }}
                        >
                            {meta.label}
                        </span>
                    </span>
                    {q.allowMultiple && (
                        <span className="font-mono text-[9px] tracking-wider uppercase text-ink-500">multi</span>
                    )}
                    {q.readOptions && (
                        <span className="font-mono text-[9px] tracking-wider uppercase text-ink-500">read-opts</span>
                    )}
                    {q.randomizeOptions && (
                        <span className="font-mono text-[9px] tracking-wider uppercase text-ink-500">randomize</span>
                    )}
                    {q.demographicField && (
                        <span className="font-mono text-[9px] tracking-wider uppercase text-accent-700">
                            demo:{q.demographicField}
                        </span>
                    )}
                    {!isActive && (
                        <span className="font-mono text-[9px] tracking-wider uppercase text-ink-400 ml-auto">disabled</span>
                    )}
                </div>

                {/* Question text */}
                <p
                    className={[
                        'text-[14px] leading-snug',
                        isActive ? 'text-ink-900' : 'text-ink-400 line-through decoration-1',
                    ].join(' ')}
                    style={{ fontFamily: '"Fraunces", "Noto Serif TC", serif', fontWeight: 450 }}
                >
                    {q.question || <span className="text-ink-300 italic">{t('row.noQuestionText')}</span>}
                </p>

                {/* Options preview */}
                {options.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                        {options.slice(0, 8).map((opt, i) => (
                            <span
                                key={opt.id || i}
                                className={[
                                    'inline-flex items-center gap-1 px-1.5 py-[2px] rounded-sm border text-[11px]',
                                    opt.isTemplate
                                        ? 'border-dashed border-ink-300 text-ink-500 bg-cream-100'
                                        : 'border-cream-300 text-ink-600 bg-cream-50',
                                ].join(' ')}
                            >
                                <span className="font-mono text-[9px] text-ink-400">{i + 1}</span>
                                <span>{truncate(opt.label || opt.value || '', 18)}</span>
                            </span>
                        ))}
                        {options.length > 8 && (
                            <span className="font-mono text-[10px] text-ink-400 self-center">
                                +{options.length - 8}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 pt-0.5">
                <button
                    onClick={onPlaceTop}
                    disabled={busy || isFirst}
                    title={isFirst ? t('row.alreadyQ1') : t('row.placeTop')}
                    className={[
                        'p-1.5 rounded-md transition-colors',
                        isFirst
                            ? 'text-ink-200 cursor-not-allowed'
                            : 'text-ink-400 hover:text-accent-700 hover:bg-accent-50',
                    ].join(' ')}
                >
                    <ArrowUpToLine size={13} strokeWidth={1.8} />
                </button>
                <button
                    onClick={onToggleActive}
                    disabled={busy}
                    title={isActive ? t('row.deactivate') : t('row.activate')}
                    className={[
                        'p-1.5 rounded-md transition-colors',
                        isActive ? 'text-ink-400 hover:text-accent-700 hover:bg-accent-50' : 'text-ink-300 hover:text-ink-700 hover:bg-cream-100',
                    ].join(' ')}
                >
                    <Power size={13} strokeWidth={1.8} />
                </button>
                <button
                    onClick={onEdit}
                    disabled={busy}
                    title={t('row.editQuestion')}
                    className="p-1.5 rounded-md text-ink-400 hover:text-accent-700 hover:bg-accent-50 transition-colors"
                >
                    <Pencil size={13} strokeWidth={1.8} />
                </button>
                <button
                    onClick={onDelete}
                    disabled={busy}
                    title={t('row.deleteQuestion')}
                    className="p-1.5 rounded-md text-ink-400 hover:text-accent-700 hover:bg-accent-50 transition-colors"
                >
                    {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} strokeWidth={1.8} />}
                </button>
            </div>
        </li>
    );
}

function truncate(s, n) {
    if (!s) return '';
    return s.length > n ? s.slice(0, n) + '…' : s;
}

// 把 local question state 轉成 API 接受的 payload。後端會自動為 customOptions 重生 id/value(只看 label)
function stripQuestionForApi(q) {
    const out = {
        question: (q.question || '').trim(),
        questionType: q.questionType || 'choice',
        isActive: q.isActive !== false,
    };
    if (out.questionType === 'choice') {
        out.useTemplate = !!q.useTemplate;
        if (q.useTemplate) {
            out.templateId = q.templateId;
        } else {
            out.customOptions = (q.customOptions || [])
                .map((o) => ({ label: (o.label || '').trim() }))
                .filter((o) => o.label);
        }
        out.allowMultiple = !!q.allowMultiple;
        out.readOptions = !!q.readOptions;
        out.randomizeOptions = !!q.randomizeOptions;
        if (q.demographicField) out.demographicField = q.demographicField;
        if (q.title) out.title = q.title;
        if (q.demographicType) out.demographicType = q.demographicType;
    } else {
        out.requireSummary = q.requireSummary !== false;
        if (q.aiSummaryPrompt) out.aiSummaryPrompt = q.aiSummaryPrompt;
    }
    return out;
}

// 兩題的「儲存欄位」是否一樣 — 用來決定要不要呼叫 updateQuestion
function questionDiffers(a, b) {
    if (!a || !b) return true;
    const fieldsToCheck = ['question', 'questionType', 'isActive', 'useTemplate', 'templateId',
        'allowMultiple', 'readOptions', 'randomizeOptions', 'demographicField', 'title',
        'demographicType', 'requireSummary', 'aiSummaryPrompt'];
    for (const f of fieldsToCheck) {
        if ((a[f] ?? null) !== (b[f] ?? null)) return true;
    }
    // customOptions 比 label 串
    const aOpts = (a.customOptions || []).map((o) => o.label || '').join('|');
    const bOpts = (b.customOptions || []).map((o) => o.label || '').join('|');
    if (aOpts !== bOpts) return true;
    return false;
}
